<template>
  <div class="wizard">
    <div class="wizard-header mb-4">
      <slot name="beforeTitle" />

      <slot name="title">
        <h2 class="text-h2 font-bold text-center text-black">
          {{ getStep(currentStep)?.name || '' }}
        </h2>
      </slot>

      <slot name="afterTitle" />

      <slot v-if="!hideBreadcrumbs" name="breadcrumbs">
        <div class="wizard-breadcrumbs flex items-center justify-center gap-2 mt-2">
          <template v-for="(step, index) in steps" :key="step.name">
            <button
              v-if="step.name !== currentStep && isStepDone(step.name)"
              class="text-[18px] font-medium"
              :style="{ color: '#BBB7AD' }"
              @click="goToStep(step.name)"
            >
              {{ step.name }}
            </button>
            <span
              v-else
              class="text-[18px] font-medium"
              :style="{ color: step.name === currentStep ? '#000000' : '#BBB7AD' }"
            >
        {{ step.name }}
      </span>

            <span
              v-if="index < steps.length - 1"
              class="breadcrumbs-arrow h-[14px]"
            >
        <svg
          width="8"
          height="14"
          viewBox="0 0 8 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 13L7 7L1 1"
            stroke="#141414"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
          </template>
        </div>
      </slot>
    </div>

    <div class="wizard-body">
      <slot :wizard="wizardContext" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed, provide } from 'vue'
import type {WizardContext} from "@/types/wizard.ts";

interface StepMeta {
  name: string
  validate?: () => boolean | Promise<boolean>
  loading?: boolean
}

const props = withDefaults(defineProps<{
  modelValue: string | null
  hideBreadcrumbs?: boolean
}>(), {
  hideBreadcrumbs: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'step-changed', newStep: string, oldStep: string): void
  (e: 'step-validation-failed', step: string): void
}>()

const currentStep = ref(props.modelValue ?? '')
const stepRegistry = ref<StepMeta[]>([])

const steps = computed(() => stepRegistry.value)
const hideBreadcrumbs = computed(() => props.hideBreadcrumbs)
const stepNames = computed(() => steps.value.map(s => s.name))

function registerStep(step: StepMeta) {
  if (!stepNames.value.includes(step.name)) {
    stepRegistry.value.push(step)
  }
}

function unregisterStep(name: string) {
  stepRegistry.value = stepRegistry.value.filter(s => s.name !== name)
}

const state = reactive({
  validation: {} as Record<string, boolean>,
  loading: {} as Record<string, boolean>,
})

const getStep = (name: string) => steps.value.find(s => s.name === name)
const getStepIndex = (name: string) => stepNames.value.indexOf(name)

const isFirstStep = computed(() => getStepIndex(currentStep.value) === 0)
const isLastStep = computed(() => getStepIndex(currentStep.value) === steps.value.length - 1)

function reset() {
  for (const name of stepNames.value) {
    state.validation[name] = false
    state.loading[name] = false
  }
  currentStep.value = steps.value[0]?.name ?? ''
}

function markStepValid(name: string, valid: boolean) {
  state.validation[name] = valid
}

function setStepLoading(name: string, loading: boolean) {
  state.loading[name] = loading
}

function isStepValid(name: string) {
  return !!state.validation[name]
}

function isStepDone(name: string) {
  return getStepIndex(name) < getStepIndex(currentStep.value)
}

async function canEnterStep(target: string) {
  const targetIndex = getStepIndex(target)
  for (let i = 0; i < targetIndex; i++) {
    const step = steps.value[i]
    const valid = await (step.validate?.() ?? true)
    markStepValid(step.name, valid)
    if (!valid) {
      emit('step-validation-failed', step.name)
      return false
    }
  }
  return true
}

async function goToStep(name: string) {
  if (!stepNames.value.includes(name)) return
  const oldStep = currentStep.value
  const ok = await canEnterStep(name)
  if (!ok) return
  currentStep.value = name
  emit('update:modelValue', name)
  emit('step-changed', name, oldStep)
}

function goToNextStep() {
  const currentIndex = getStepIndex(currentStep.value)
  if (currentIndex < steps.value.length - 1) {
    const next = steps.value[currentIndex + 1]?.name
    if (next) goToStep(next)
  }
}

function goToPrevStep() {
  const currentIndex = getStepIndex(currentStep.value)
  if (currentIndex > 0) {
    const prev = steps.value[currentIndex - 1]?.name
    if (prev) goToStep(prev)
  }
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal && newVal !== currentStep.value) {
      currentStep.value = newVal
    }
  }
)

const wizardContext: WizardContext = {
  currentStep,
  steps,
  isFirstStep,
  isLastStep,
  isStepValid,
  isStepDone,
  getStep,
  getStepIndex,
  goToStep,
  goToNextStep,
  goToPrevStep,
  reset,
  markStepValid,
  setStepLoading,
  loadingStates: state.loading,
  validationStates: state.validation,
  registerStep,
  unregisterStep,
}

provide('wizard', wizardContext)
</script>

<style scoped>
.wizard {
  display: flex;
  flex-direction: column;
}
</style>
